<template>
  <v-dialog
    :value="dialog"
    :max-width="400"
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined>
      <v-card-title>
        {{ dialogTitle }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="form" @submit.stop.prevent>
          <v-container>
            <v-row dense align="center" justify="end">
              <v-col>
                <v-text-field
                  type="text"
                  v-model.trim="editedSeason.name"
                  :rules="rules.name"
                  tabindex="1"
                  clearable
                  autofocus
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-end">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text :disabled="!isModelChanged()" tabindex="2" color="primary" @click="submit">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import PBtnIcon from '@/components/PBtnIcon'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import errorMixin from '@/mixins/error.mixin'


export default {
  components: {
    PBtnIcon
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    errorMixin
  ],


  props: {
    season: Object
  },


  data () {
    return {
      editedSeason: {},
      defaultSeason: {
        name: ''
      },
      existingSeasonNames: []
    }
  },


  computed: {
    ...mapGetters('season', ['seasons']),

    dialogTitle () {
      return this.season ? this.$t('season.editSeason') : this.$t('season.createSeason')
    },

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required(),
          rule.unique(this.existingSeasonNames, this.$t('season.nameExists'))
        ]
      }
    }
  },


  methods: {
    ...mapActions('season', ['createSeason', 'updateSeason', 'setCurrentSeason']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.season) {
        this.removeFromChanges(this.season)
      }
      this.existingSeasonNames = []
      this.editedSeason = this.getObjectCopy(this.season || this.defaultSeason)
      this.initWatchedModel(this.editedSeason)
      this.resetValidation()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const season = this.getObjectCopy(this.editedSeason)

        if (season.id) {
          await this.updateSeason(season)
          this.showSnackbarSuccess(this.$t('season.seasonUpdated'))
        } else {
          await this.createSeason(season)
          if (this.seasons.length === 1) {
            this.setCurrentSeason()
          }
          this.showSnackbarSuccess(this.$t('season.seasonCreated'))
        }

        this.closeDialog()
      } catch (error) {
        if (this.is409ConflictError(error)) {
          this.hideSnackbar()
          this.existingSeasonNames.push(this.editedSeason.name)
          this.validate(this.rulesToApply)
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    }
  }
}
</script>
